import React from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  InfoCard, Link, StructuredMetadataTable,
} from '@backstage/core-components';
import {
  Alert, Box, Chip, Grid, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography,
} from '@mui/material';
import {
  catalogApiRef, EntityRefLink, useEntity,
} from '@backstage/plugin-catalog-react';
import {
  GitHubRepositoryResourceEntity, OktaUserEntity,
} from '@eng-portal/czi-extensions-common';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { omit } from 'lodash';
import useAsync from 'react-use/lib/useAsync';
import { CardTitle } from './CardTitle';

function useGithubUsers(logins: string[]): Map<string, OktaUserEntity> {
  const catalogApi = useApi(catalogApiRef);
  const uniqueLogins = [...new Set(logins.map(login => login.toLowerCase()))].sort();

  const { value } = useAsync(async () => {
    if (uniqueLogins.length === 0) return new Map<string, OktaUserEntity>();

    try {
      const { items } = await catalogApi.getEntities({
        filter: { kind: 'User', 'spec.profile.githubUsername': uniqueLogins },
        fields: ['kind', 'metadata.namespace', 'metadata.name', 'metadata.title', 'spec.profile'],
      });
      const usersByLogin = new Map<string, OktaUserEntity>();
      for (const user of items as OktaUserEntity[]) {
        const login = user.spec.profile.githubUsername;
        if (login) usersByLogin.set(login.toLowerCase(), user);
      }
      return usersByLogin;
    } catch {
      return new Map<string, OktaUserEntity>();
    }
  }, [catalogApi, uniqueLogins.join(',')]);

  return value ?? new Map();
}

function GithubUserLink({
  login,
  users,
}: {
  login: string;
  users: Map<string, OktaUserEntity>;
}): JSX.Element {
  const user = users.get(login.toLowerCase());
  return user ? (
    <EntityRefLink
      entityRef={user}
      title={user.spec.profile.displayName || user.metadata.title || login}
    />
  ) : (
    <Typography variant="body2" component="span">{login}</Typography>
  );
}

export function GithubRepositoryAboutCard(): JSX.Element {
  const { entity: repo } = useEntity<GitHubRepositoryResourceEntity>();
  const users = useGithubUsers([
    ...(repo?.spec.cziOwnerLogins ?? []),
    ...(repo?.spec.repoAdmins ?? []),
    ...(repo?.spec.topContributors ?? []).map(contributor => contributor.login),
  ]);

  if (!repo) {
    return <Alert severity="error">Repository not found</Alert>;
  }

  const {
    metadata: { name, title },
    spec,
  } = repo;

  const {
    topContributors, securityVulnerabilities, cziOwners, cziOwnerLogins, repoAdmins,
  } = spec;

  const metadata: Record<string, any> = {
    ...omit(
      spec,
      'type',
      'owner',
      'dependsOn',
      'scorecard',
      'branchProtection',
      'topContributors',
      'securityVulnerabilities',
      'cziOwners',
      'cziOwnerLogins',
      'repoAdmins',
      'codeownersContent',
      'codeownersGlobalOwners',
    ),
  };

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const totalVulnerabilities = securityVulnerabilities?.reduce((sum, vuln) => sum + vuln.count, 0) || 0;

  // Extract GitHub URL from metadata links and construct security URL
  const githubUrl = repo.metadata.links?.find(link => link.type === 'Github')?.url;
  const securityUrl = githubUrl ? `${githubUrl}/security` : undefined;

  return (
    <InfoCard
      title={<CardTitle title={title || name} />}
    >
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item md={12} xl={12}>
          <List>
            <ListItem>
              <ListItemIcon>
                <Tooltip title="Repository Metadata">
                  <AccountTreeIcon />
                </Tooltip>
              </ListItemIcon>
              <StructuredMetadataTable metadata={metadata} />
            </ListItem>
            <br />
            
            <ListItem>
              <ListItemIcon>
                <Tooltip title="Security Vulnerabilities">
                  <SecurityIcon />
                </Tooltip>
              </ListItemIcon>
              <ListItemText>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle2">
                    Security Vulnerabilities
                  </Typography>
                  {securityUrl && totalVulnerabilities > 0 && (
                    <Link to={securityUrl} target="_blank" style={{ fontSize: '0.875rem' }}>
                      View on GitHub →
                    </Link>
                  )}
                </Box>
                {totalVulnerabilities === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No open security vulnerabilities
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {securityVulnerabilities?.map((vuln) => (
                      <Chip
                        key={vuln.severity}
                        label={`${vuln.severity.toUpperCase()}: ${vuln.count}`}
                        color={getSeverityColor(vuln.severity)}
                        size="small"
                      />
                    ))}
                    <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                      (Total: {totalVulnerabilities})
                    </Typography>
                  </Box>
                )}
              </ListItemText>
            </ListItem>
            <br />

            {((cziOwnerLogins && cziOwnerLogins.length > 0) || (cziOwners && cziOwners.length > 0)) && (
              <>
                <ListItem>
                  <ListItemIcon>
                    <Tooltip title="CZI Owners">
                      <AdminPanelSettingsIcon />
                    </Tooltip>
                  </ListItemIcon>
                  <ListItemText>
                    <Typography variant="subtitle2" gutterBottom>
                      CZI Owners
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {cziOwnerLogins && cziOwnerLogins.length > 0
                        ? cziOwnerLogins.map((login) => (
                          <Box key={login}>
                            <GithubUserLink login={login} users={users} />
                          </Box>
                        ))
                        // Entities ingested before owner logins were recorded only have emails.
                        : cziOwners?.map((email) => (
                          <Typography key={email} variant="body2">
                            {email}
                          </Typography>
                        ))}
                    </Box>
                  </ListItemText>
                </ListItem>
                <br />
              </>
            )}

            {repoAdmins && repoAdmins.length > 0 && (
              <>
                <ListItem>
                  <ListItemIcon>
                    <Tooltip title="Repo Admins">
                      <SupervisorAccountIcon />
                    </Tooltip>
                  </ListItemIcon>
                  <ListItemText>
                    <Typography variant="subtitle2" gutterBottom>
                      Repo Admins
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {repoAdmins.map((login) => (
                        <Box key={login}>
                          <GithubUserLink login={login} users={users} />
                        </Box>
                      ))}
                    </Box>
                  </ListItemText>
                </ListItem>
                <br />
              </>
            )}

            {topContributors && topContributors.length > 0 && (
              <>
                <ListItem>
                  <ListItemIcon>
                    <Tooltip title="Top Contributors">
                      <PeopleIcon />
                    </Tooltip>
                  </ListItemIcon>
                  <ListItemText>
                    <Typography variant="subtitle2" gutterBottom>
                      Top Contributors
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {topContributors.map((contributor) => (
                        <Box
                          key={contributor.login}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={contributor.avatarUrl}
                            alt={contributor.login}
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              objectFit: 'cover',
                            }}
                          />
                          <GithubUserLink login={contributor.login} users={users} />
                          <Typography variant="body2" color="textSecondary">
                            ({contributor.contributions} contributions)
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </ListItemText>
                </ListItem>
                <br />
              </>
            )}
          </List>
        </Grid>
      </Grid>
    </InfoCard>
  );
}

